/**
 * Servizio di sanitizzazione per prevenire XSS attacks
 * Utilizza DOMPurify per pulire HTML e input utente
 */

import DOMPurify from 'dompurify';

class Sanitizer {
  /**
   * Sanitizza HTML completo
   * Permette solo tag sicuri e rimuove script/eventi pericolosi
   */
  sanitizeHTML(dirty: string): string {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'div', 'span', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: ['href', 'title', 'target', 'class'],
      ALLOW_DATA_ATTR: false,
      USE_PROFILES: { html: true }
    });
  }

  /**
   * Sanitizza testo semplice
   * Rimuove tutti i tag HTML e mantiene solo il testo
   */
  sanitizeText(dirty: string): string {
    return DOMPurify.sanitize(dirty, { 
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true 
    });
  }

  /**
   * Sanitizza URL
   * Permette solo protocolli sicuri
   */
  sanitizeURL(url: string): string {
    // Permette solo http, https, mailto
    const cleaned = DOMPurify.sanitize(url, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    });
    
    // Validazione aggiuntiva
    try {
      const urlObj = new URL(cleaned);
      if (!['http:', 'https:', 'mailto:'].includes(urlObj.protocol)) {
        return '';
      }
      return cleaned;
    } catch {
      // Se non è un URL valido, restituisci stringa vuota
      return '';
    }
  }

  /**
   * Sanitizza input per uso in attributi HTML
   * Escape di caratteri speciali
   */
  sanitizeAttribute(dirty: string): string {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    }).replace(/[<>"'&]/g, (char) => {
      const entities: { [key: string]: string } = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;'
      };
      return entities[char] || char;
    });
  }

  /**
   * Sanitizza nome file
   * Rimuove caratteri pericolosi per filesystem
   */
  sanitizeFileName(fileName: string): string {
    // Rimuove caratteri pericolosi e path traversal
    return fileName
      .replace(/[^a-zA-Z0-9._\-]/g, '_')
      .replace(/\.{2,}/g, '_')
      .replace(/^\./, '_')
      .substring(0, 255); // Limita lunghezza
  }

  /**
   * Sanitizza oggetto JSON
   * Pulisce ricorsivamente tutte le stringhe
   */
  sanitizeJSON(obj: any): any {
    if (typeof obj === 'string') {
      return this.sanitizeText(obj);
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeJSON(item));
    }
    
    if (obj !== null && typeof obj === 'object') {
      const sanitized: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          // Sanitizza anche le chiavi
          const sanitizedKey = this.sanitizeAttribute(key);
          sanitized[sanitizedKey] = this.sanitizeJSON(obj[key]);
        }
      }
      return sanitized;
    }
    
    return obj;
  }
}

// Singleton instance
const sanitizer = new Sanitizer();

export default sanitizer;